
import { useAuth } from '@/context/auth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { TeacherMonthlyReport, ReportDetail, Commission } from '@/types/salary';

export function useTeacherSalary(selectedMonth: number, selectedYear: number) {
  const { user } = useAuth();

  const { data: teacherProfile } = useQuery({
    queryKey: ['teacher-profile'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('teacher_id')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user
  });

  const { data: monthlyReport, isLoading, refetch } = useQuery({
    queryKey: ['teacher-monthly-report', selectedMonth, selectedYear, teacherProfile?.teacher_id],
    queryFn: async () => {
      if (!teacherProfile?.teacher_id) return null;

      const { data, error } = await supabase
        .from('teacher_monthly_reports')
        .select('*')
        .eq('month', selectedMonth)
        .eq('year', selectedYear)
        .eq('teacher_id', teacherProfile.teacher_id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }
      
      // Transform the data to ensure commission_details is properly typed
      return {
        ...data,
        commission_details: data.commission_details 
          ? (data.commission_details as unknown as Commission[])
          : null
      } as TeacherMonthlyReport;
    },
    enabled: !!teacherProfile?.teacher_id
  });

  const reportDetails = monthlyReport?.report_details 
    ? (typeof monthlyReport.report_details === 'string' 
        ? JSON.parse(monthlyReport.report_details) 
        : monthlyReport.report_details) 
    : [];

  return {
    monthlyReport,
    isLoading,
    refetch,
    reportDetails: reportDetails.length 
      ? [...reportDetails].sort((a: ReportDetail, b: ReportDetail) => {
          return new Date(a.teaching_date).getTime() - new Date(b.teaching_date).getTime();
        }) 
      : []
  };
}
